import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";

import { FILTER_ITEM_CARD_CLASS_NAME } from "../../constants";
import type { ChipStatus } from "../../types/chip";
import type { FilterChipVariant } from "../../types/commonEnums";
import { FiltersChip } from "../FiltersChip/FiltersChip";

import { StyledChipContainer, StyledDelimeter, StyledShowMoreButton, StyledShowMoreIconButton } from "./styles";

export type ChipItem = {
    text: string;
    id: string;
    code?: string;
    iconName?: IconName;
    variant?: FilterChipVariant;
    chipStatus?: ChipStatus;
    iconSrc?: string;
    tooltipLines?: readonly string[];
};

interface IProps {
    items: readonly ChipItem[];
    maxItems: number;
    delimiter?: string;
    isTabExpanded?: boolean;
    hideActionButton?: boolean;
    maxChipTextSize?: number;
    onDelete?: (itemId: string, item: ChipItem) => void;
}

export const LimitedChipItems: React.FC<IProps> = ({
    items,
    maxItems,
    delimiter,
    isTabExpanded,
    hideActionButton,
    maxChipTextSize,
    onDelete,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const firstChipRef = useRef<HTMLDivElement>(null);
    const shouldScrollBackRef = useRef(false);

    const isOverLimit = items.length > maxItems;
    const displayedItems = isCollapsed ? items.slice(0, maxItems) : items;

    useEffect(() => {
        if (!isTabExpanded) {
            setIsCollapsed(true);
        }
    }, [isTabExpanded]);

    // Collapsing a long list removes everything between the first chip and the button the user
    // just clicked, leaving the viewport parked below the filter. Bring the filter back into view.
    useLayoutEffect(() => {
        if (!isCollapsed || !shouldScrollBackRef.current) {
            return;
        }
        shouldScrollBackRef.current = false;

        const firstChip = firstChipRef.current;
        const target = firstChip?.closest(`.${FILTER_ITEM_CARD_CLASS_NAME}`) ?? firstChip;
        target?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }, [isCollapsed]);

    const handleShowMore = () => {
        shouldScrollBackRef.current = !isCollapsed;
        setIsCollapsed(!isCollapsed);
    };

    const renderShowMoreButton = () =>
        isCollapsed ? (
            <StyledShowMoreIconButton
                iconName="add"
                iconColor={colorsPalettes.blue[500]}
                type="flat"
                onClick={handleShowMore}
            >
                {items.length - maxItems}
            </StyledShowMoreIconButton>
        ) : (
            <StyledShowMoreButton
                type="flat"
                onClick={handleShowMore}
            >
                {translate("universal.button.showless")}
            </StyledShowMoreButton>
        );

    return (
        <>
            {displayedItems.map((item, index) => (
                <StyledChipContainer
                    key={item.id}
                    ref={index === 0 ? firstChipRef : undefined}
                >
                    <FiltersChip
                        id={item.id}
                        iconName={item.iconName}
                        code={item.code}
                        text={item.text}
                        variant={item.variant}
                        chipStatus={item.chipStatus}
                        hideActionButton={hideActionButton}
                        maxChipTextSize={maxChipTextSize}
                        iconSrc={item.iconSrc}
                        tooltipLines={item.tooltipLines}
                        onClose={() => onDelete?.(item.id, item)}
                    />
                    {delimiter && index < items.length - 1 ? (
                        <StyledDelimeter
                            fontVariant="listSectionTitle"
                            color={colorsPalettes.carbon[300]}
                        >
                            {delimiter}
                        </StyledDelimeter>
                    ) : null}
                </StyledChipContainer>
            ))}
            {isOverLimit ? renderShowMoreButton() : null}
        </>
    );
};
